// ============================================
// START SCREEN - タイトル画面の描画とアニメーション
// ============================================

import { getP5 } from '../../core/p5-instance';
import { getHighScore, getTitleTimer } from '../../store/index';
import { drawShipBomber } from '../../features/player/renderer/ship-renderer';

// ============================================
// isGameStartButtonClicked() - GAME STARTボタンのクリック判定
// ============================================
export function isGameStartButtonClicked(): boolean {
    const p = getP5();
    const btnY1 = p.height * 0.78;
    const btnX = p.width / 2;
    const btnW = 200;
    const btnH = 50;
    return p.mouseX > btnX - btnW / 2 && p.mouseX < btnX + btnW / 2 &&
           p.mouseY > btnY1 && p.mouseY < btnY1 + btnH;
}

// ============================================
// isGalleryButtonClicked() - GALLERYボタンのクリック判定
// ============================================
export function isGalleryButtonClicked(): boolean {
    const p = getP5();
    const btnY2 = p.height * 0.88;
    const btnX = p.width / 2;
    const btnW = 200;
    const btnH = 50;
    return p.mouseX > btnX - btnW / 2 && p.mouseX < btnX + btnW / 2 &&
           p.mouseY > btnY2 && p.mouseY < btnY2 + btnH;
}

// ============================================
// isClearHighScoreButtonClicked() - CLRボタンのクリック判定
// ============================================
export function isClearHighScoreButtonClicked(): boolean {
    const p = getP5();
    // ハイスコアが0の場合はボタンが表示されないのでfalse
    if (getHighScore() <= 0) {
        return false;
    }

    // CLRボタンの位置（画面上部、ハイスコア右側）
    const scoreY = 45;
    const btnX = p.width / 2 + 85;  // スコア表示の右側
    const btnW = 36;
    const btnH = 20;
    return p.mouseX > btnX - btnW / 2 && p.mouseX < btnX + btnW / 2 &&
           p.mouseY > scoreY - btnH / 2 && p.mouseY < scoreY + btnH / 2;
}

// ============================================
// drawStartScreen() - タイトル画面の描画
// ============================================
export function drawStartScreen(): void {
    const p = getP5();
    p.textAlign(p.CENTER, p.CENTER);
    p.textFont('Orbitron');

    // 背景装飾 - 流れるライン
    p.stroke(0, 255, 255, 30);
    p.strokeWeight(1);
    for (let i = 0; i < 8; i++) {
        const y = (getTitleTimer() * 2 + i * 100) % (p.height + 200) - 100;
        const x1 = p.sin(i * 0.5 + getTitleTimer() * 0.02) * 100 + p.width * 0.3;
        const x2 = p.sin(i * 0.5 + getTitleTimer() * 0.02 + 1) * 100 + p.width * 0.7;
        p.line(x1, y, x2, y + 50);
    }

    // 装飾円
    p.noFill();
    p.stroke(0, 255, 255, 20);
    p.strokeWeight(2);
    const circleSize = 200 + p.sin(getTitleTimer() * 0.03) * 20;
    p.circle(p.width / 2, p.height / 3, circleSize);
    p.circle(p.width / 2, p.height / 3, circleSize * 0.7);

    // タイトルのグロー効果
    for (let i = 5; i > 0; i--) {
        p.fill(0, 255, 255, 25);
        p.textSize(48 + i * 3);
        p.text('LANE', p.width / 2, p.height / 3 - 35);
    }
    for (let i = 5; i > 0; i--) {
        p.fill(255, 0, 255, 25);
        p.textSize(48 + i * 3);
        p.text('SHOOTER', p.width / 2, p.height / 3 + 35);
    }

    // タイトル本体
    p.fill(0, 255, 255);
    p.textSize(48);
    p.text('LANE', p.width / 2, p.height / 3 - 35);

    p.fill(255, 0, 255);
    p.text('SHOOTER', p.width / 2, p.height / 3 + 35);

    // サブタイトルライン
    p.stroke(255, 255, 255, 100);
    p.strokeWeight(1);
    const lineW = 120;
    p.line(p.width / 2 - lineW, p.height / 3 + 75, p.width / 2 + lineW, p.height / 3 + 75);

    // バージョン/サブタイトル
    p.noStroke();
    p.fill(150);
    p.textSize(12);
    p.text('CYBER EDITION', p.width / 2, p.height / 3 + 90);

    // 装飾用の宇宙船（回転）
    p.push();
    p.translate(p.width / 2, p.height * 0.52);
    const shipHover = p.sin(getTitleTimer() * 0.05) * 8;
    p.translate(0, shipHover);
    p.rotate(p.sin(getTitleTimer() * 0.02) * 0.1);
    drawTitleShip(0, 0, 70);
    p.pop();

    // ハイスコア表示（画面上部、常に表示）
    p.fill(255, 200, 0, 200);
    p.textSize(12);
    p.text('HIGH SCORE', p.width / 2, 25);
    p.textSize(18);
    p.text(p.floor(getHighScore()).toString().padStart(8, '0'), p.width / 2, 45);

    // CLRボタン（ハイスコア > 0 の場合のみ表示）
    if (getHighScore() > 0) {
        const scoreY = 45;
        const btnX = p.width / 2 + 85;
        p.fill(80, 80, 80, 150);
        p.stroke(120, 120, 120, 150);
        p.strokeWeight(1);
        p.rect(btnX - 18, scoreY - 10, 36, 20, 4);
        p.fill(150, 150, 150);
        p.noStroke();
        p.textSize(10);
        p.text('CLR', btnX, scoreY);
    }

    // ボタンのパルスアニメーション
    const pulse = p.sin(getTitleTimer() * 0.1) * 0.4 + 0.6;

    // 「GAME START」ボタン
    const btnY1 = p.height * 0.78;
    p.fill(0, 255, 255, 30 * pulse);
    p.stroke(0, 255, 255, 150 * pulse);
    p.strokeWeight(2);
    p.rect(p.width / 2 - 100, btnY1, 200, 50, 25);

    p.fill(255, 255, 255, 255 * pulse);
    p.noStroke();
    p.textSize(20);
    p.text('GAME START', p.width / 2, btnY1 + 25);

    // 「GALLERY」ボタン
    const btnY2 = p.height * 0.88;
    p.fill(255, 0, 255, 30 * pulse);
    p.stroke(255, 0, 255, 150 * pulse);
    p.strokeWeight(2);
    p.rect(p.width / 2 - 100, btnY2, 200, 50, 25);

    p.fill(255, 255, 255, 255 * pulse);
    p.noStroke();
    p.textSize(20);
    p.text('GALLERY', p.width / 2, btnY2 + 25);

    // フッター
    p.fill(80);
    p.textSize(10);
    p.text('© 2024 LANE SHOOTER', p.width / 2, p.height - 20);
}

// ============================================
// drawTitleShip() - タイトル用の宇宙船描画
// ship-renderer.ts のBOMBER機体デザインを使用
// ============================================
export function drawTitleShip(x: number, y: number, size: number): void {
    // ship-renderer.ts の統一されたデザインを使用
    drawShipBomber(x, y, size, {
        showEngine: true,
        showGlow: true
    });
}
